import { useEffect } from 'react';
import logger from '../utils/Logger';
import {
  getCookie,
  setCookie,
} from '../utils/cookieUtils';
import {
  WS_ERROR_MESSAGES,
  extractResponseCode,
  isPlanLimitErrorCode,
  normalizeWarningPayload,
  handleWarningToastAndCountdown,
  isNearTimeLimitExpiry,
} from '../utils/wsErrorCodes';
import { storeConfigurationResponse, getStoredToken } from '../utils/tokenUtils';

const COMPONENT_NAME = 'useWebSocketConnection';

/**
 * Custom hook to manage WebSocket connection lifecycle
 * Handles connection establishment, message handling, error handling, and reconnection logic
 * 
 * @param {Object} config - Configuration object containing all required dependencies
 * @param {string} config.sessionId - Current session ID
 * @param {string} config.conversationId - Current conversation ID
 * @param {string} config.conversationIdFromUrl - Conversation ID from URL
 * @param {string} config.userId - Authenticated user ID
 * @param {Ref} config.socketRef - WebSocket reference
 * @param {Ref} config.assemblySocketRef - Assembly WebSocket reference
 * @param {Ref} config.userInitiatedStopRef - Flag to track user-initiated stops
 * @param {Ref} config.wsConnectLatchRef - Latch to prevent duplicate connections
 * @param {Ref} config.persistentDataRequestedRef - Flag to track persistent data requests
 * @param {Ref} config.webSocketConnectedRef - Flag to track connection state
 * @param {Ref} config.isNewSessionRef - Flag to track new sessions
 * @param {string} config.websocketBaseUrl - WebSocket server URL
 * @param {Function} config.getAuthenticatedUserId - Function to get authenticated user ID
 * @param {Function} config.requestToken - Function to request token
 * @param {Function} config.handlePersistentData - Function to handle persistent data
 * @param {Function} config.startTranscriptionStreamWithToken - Function to start transcription
 * @param {Function} config.startTokenExpiryTimer - Function to start token expiry timer
 * @param {Function} config.getSessionIdForConversation - Function to get session ID for conversation
 * @param {Function} config.setSessionId - State setter for session ID
 * @param {Function} config.setDateString - State setter for date string
 * @param {Function} config.setTimeString - State setter for time string
 * @param {Function} config.showPlanWarningToast - Function to show plan warning toast
 * @param {Function} config.schedulePlanCountdownFromPayload - Function to schedule plan countdown
 * @param {Function} config.scheduleTimeLimitDisconnect - Function to schedule time limit disconnect
 * @param {Function} config.reconnectWebSocketLocal - Function to reconnect WebSocket locally
 * @param {Function} config.handlePlanLimitReached - Function to handle plan limit reached (stops recording)
 * @param {boolean} config.isLive - Flag to track if currently live
 */
export const useWebSocketConnection = (config) => {
  const {
    sessionId,
    conversationId,
    conversationIdFromUrl,
    userId,
    socketRef,
    assemblySocketRef,
    userInitiatedStopRef,
    wsConnectLatchRef,
    persistentDataRequestedRef,
    webSocketConnectedRef,
    isNewSessionRef,
    websocketBaseUrl,
    getAuthenticatedUserId,
    requestToken,
    handlePersistentData,
    startTranscriptionStreamWithToken,
    startTokenExpiryTimer,
    getSessionIdForConversation,
    setSessionId,
    setDateString,
    setTimeString,
    showPlanWarningToast,
    schedulePlanCountdownFromPayload,
    scheduleTimeLimitDisconnect,
    reconnectWebSocketLocal,
    handlePlanLimitReached,
    isLive,
  } = config;

  // Validate all required config parameters
  useEffect(() => {
    const requiredFunctions = [
      'getAuthenticatedUserId',
      'requestToken',
      'handlePersistentData',
      'startTranscriptionStreamWithToken',
      'startTokenExpiryTimer',
      'getSessionIdForConversation',
      'setSessionId',
      'setDateString',
      'setTimeString',
      'showPlanWarningToast',
      'schedulePlanCountdownFromPayload',
      'scheduleTimeLimitDisconnect',
      'reconnectWebSocketLocal',
    ];

    const requiredRefs = [
      'socketRef',
      'assemblySocketRef',
      'userInitiatedStopRef',
      'wsConnectLatchRef',
      'persistentDataRequestedRef',
      'webSocketConnectedRef',
      'isNewSessionRef',
    ];

    const missingFunctions = requiredFunctions.filter((fn) => typeof config[fn] !== 'function');
    const missingRefs = requiredRefs.filter((ref) => !config[ref]);

    if (missingFunctions.length > 0 || missingRefs.length > 0) {
      logger.warn('useWebSocketConnection: Missing dependencies', {
        component: COMPONENT_NAME,
        action: 'MISSING_DEPENDENCIES',
        missingFunctions,
        missingRefs,
      });

      if (missingFunctions.length > 0) {
        console.warn('[useWebSocketConnection] Missing required functions:', missingFunctions);
      }
      if (missingRefs.length > 0) {
        console.warn('[useWebSocketConnection] Missing required refs:', missingRefs);
      }
    }
  }, [config]);

  useEffect(() => {
    // Don't connect if user initiated stop
    if (userInitiatedStopRef.current || window.isStoppingSession) {
      return;
    }

    // Don't connect if session state has been cleaned up (both null) but we're on a non-record page
    // This prevents stale conversationIdFromUrl from triggering a spurious connection during navigation
    if (!conversationId && !sessionId && !isLive) {
      return;
    }

    // WebSocket should never open without a valid conversation_id
    const uid = getAuthenticatedUserId();
    const effectiveId = conversationId || conversationIdFromUrl;
    if (!uid || !effectiveId || effectiveId === 'null') {
      return; // Wait — don't connect yet
    }

    const key = JSON.stringify({ sid: sessionId || null, cid: effectiveId, uid });

    // Check if already connected
    if (
      socketRef.current &&
      (socketRef.current.readyState === WebSocket.OPEN || socketRef.current.readyState === WebSocket.CONNECTING)
    ) {
      if (wsConnectLatchRef.current.key === key) {
        wsConnectLatchRef.current.inFlight = false;
      }
      return;
    }

    const state = socketRef.current?.readyState;
    if (webSocketConnectedRef.current && (state === WebSocket.CONNECTING || state === WebSocket.OPEN)) {
      return;
    }
    if (wsConnectLatchRef.current.inFlight && wsConnectLatchRef.current.key === key) {
      return;
    }

    // Clean up old socket if exists
    if (socketRef.current) {
      if (state === WebSocket.OPEN || state === WebSocket.CLOSING) {
        try {
          socketRef.current.onclose = null;
          socketRef.current.onerror = null;
          socketRef.current.onopen = null;
          socketRef.current.onmessage = null;
          socketRef.current.close();
        } catch {}
      } else if (state === WebSocket.CONNECTING) {
        socketRef.current.onopen = null;
        socketRef.current.onclose = null;
        socketRef.current.onerror = null;
        socketRef.current.onmessage = null;
        return;
      }
      socketRef.current = null;
      webSocketConnectedRef.current = false;
    }

    wsConnectLatchRef.current = { key, inFlight: true };

    const tokenPayload = {
      user_id: uid,
      session_id: sessionId || effectiveId,
      conversation_id: effectiveId,
      user_type: 'speaker',
      connection_source: 'web',
    };
    const token = btoa(JSON.stringify(tokenPayload));
    if (!token || !websocketBaseUrl) {
      wsConnectLatchRef.current.inFlight = false;
      return;
    }

    const wsUrl = `${websocketBaseUrl}?token=${token}`;
    const socket = new window.WebSocket(wsUrl);
    socketRef.current = socket;

    // ========== SOCKET ONOPEN HANDLER ==========
    socket.onopen = () => {
      logger.info('Main WebSocket connection established', {
        component: COMPONENT_NAME,
        action: 'WEBSOCKET_CONNECTED',
        userId: getAuthenticatedUserId(),
        context: {
          conversationId: conversationId || getCookie('conversation_id'),
          conversationIdFromUrl,
          sessionId,
          wsUrl: wsUrl ? wsUrl.split('?')[0] : null,
          readyState: socket.readyState,
          protocol: socket.protocol || null,
          browserOnline: navigator.onLine,
        },
      });
      console.log('✅ [WebSocket] Connected to server');
      webSocketConnectedRef.current = true;
      socketRef.lastOpenTime = Date.now();

      if (!persistentDataRequestedRef.current) {
        const effectiveConvId = conversationId || conversationIdFromUrl;
        if (!effectiveConvId || effectiveConvId === 'null') {
          logger.error('WebSocket open — conversation_id not ready, skipping requestToken', {
            component: COMPONENT_NAME,
            action: 'TOKEN_REQUEST_SKIPPED',
            context: { conversationId, conversationIdFromUrl },
          });
          return;
        }

        // Determine the appropriate scenario based on session context
        const isCreatingNew = sessionStorage.getItem('session_creation_flag') === 'true' || isNewSessionRef.current;
        const scenario = isCreatingNew ? 'new' : 'refresh';
        requestToken(scenario);
        persistentDataRequestedRef.current = true;

        // Reset the flags after the request is made
        if (isCreatingNew) {
          isNewSessionRef.current = false;
          sessionStorage.removeItem('session_creation_flag');
        }
      }

      // Setup ping interval
      const pingInterval = setInterval(() => {
        if (socket.readyState === WebSocket.OPEN) {
          const userId = getAuthenticatedUserId();
          socket.send(
            JSON.stringify({
              action: 'ping',
              session_id: sessionId,
              user_id: userId,
              conversation_id: conversationId,
              user_type: 'speaker',
            })
          );
        } else {
          clearInterval(pingInterval);
        }
      }, 5000);
      socket.pingInterval = pingInterval;

      if (wsConnectLatchRef.current.key === key) {
        wsConnectLatchRef.current.inFlight = false;
      }
    };

    // ========== SOCKET ONMESSAGE HANDLER ==========
    socket.onmessage = (message) => {
      try {
        if (!message.data) return;
        if (message.data instanceof ArrayBuffer || message.data instanceof Blob) return;
        if (typeof message.data !== 'string') return;

        const data = JSON.parse(message.data);
        if (data.action === 'pong') return;

        const planInfo = normalizeWarningPayload(data);

        if (data.warning_message) {
          logger.info('Per-conversation warning message received', {
            component: COMPONENT_NAME,
            action: 'TIME_LIMIT_WARNING',
            userId: getAuthenticatedUserId(),
            context: {
              warningMessage: data.warning_message,
              conversationId: conversationId || getCookie('conversation_id'),
              sessionId: sessionId || getCookie('session_id'),
            },
          });
        }

        handleWarningToastAndCountdown(data, planInfo, showPlanWarningToast, schedulePlanCountdownFromPayload);

        // NFGI-908: Handle expires_at field to schedule Deepgram WebSocket disconnection
        if (data.action === 'configuration_speaker' && data.expires_at) {
          scheduleTimeLimitDisconnect(data.expires_at);
        }

        const responseCode = extractResponseCode(data);
        const serverMessage = data.message || data.data?.message;

        if (isPlanLimitErrorCode(responseCode)) {
          if (typeof handlePlanLimitReached === 'function') {
            const errorMessage = WS_ERROR_MESSAGES[responseCode] || serverMessage || 'Your plan limit has been reached. We are stopping and saving your recording.';
            handlePlanLimitReached(errorMessage);
          }
          return;
        }

        if (data.action === 'persistent_data' || data.action === 'persistentdata' || data.type === 'persistentdata') {
          handlePersistentData(data.data || data);
          return;
        }

        if (data.action === 'configuration_speaker') {
          const modelDetails = data.model_details || {};

          // Set start time from created_at (persists across refreshes)
          if (data.created_at) {
            let createdAtUTC = data.created_at;
            if (!createdAtUTC.endsWith('Z') && !createdAtUTC.includes('+') && !createdAtUTC.includes('-', 10)) {
              createdAtUTC = createdAtUTC + 'Z';
            }
            const createdDate = new Date(createdAtUTC);
            if (!isNaN(createdDate.getTime())) {
              setDateString(createdDate.toLocaleDateString());
              setTimeString(
                createdDate
                  .toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })
                  .toUpperCase()
              );
            }
          }

          if (modelDetails.status === 'new') {
            // New token - store in cookies and connect
            storeConfigurationResponse(data);

            if (!sessionId && (conversationId || conversationIdFromUrl)) {
              const convId = conversationId || conversationIdFromUrl;

              (async () => {
                try {
                  const actualSessionId = await getSessionIdForConversation(convId);
                  if (actualSessionId) {
                    setCookie('session_id', actualSessionId);
                    localStorage.setItem('session_id', actualSessionId);
                    setSessionId(actualSessionId);
                  }
                } catch (error) {
                  console.error('❌ [Token Response] Failed to fetch session_id:', error);
                }
              })();
            }

            if (!userInitiatedStopRef.current && !window.isStoppingSession) {
              if (!assemblySocketRef.current || assemblySocketRef.current.readyState !== WebSocket.OPEN) {
                startTranscriptionStreamWithToken(modelDetails.token, modelDetails.model);
                startTokenExpiryTimer();
              } else {
                startTokenExpiryTimer();
              }
            }
          } else if (modelDetails.status === 'existing') {
            // Existing token - update timestamps and check if this is session restoration
            storeConfigurationResponse(data);

            if (!sessionId && (conversationId || conversationIdFromUrl)) {
              const convId = conversationId || conversationIdFromUrl;
              (async () => {
                try {
                  const actualSessionId = await getSessionIdForConversation(convId);
                  if (actualSessionId) {
                    setCookie('session_id', actualSessionId);
                    localStorage.setItem('session_id', actualSessionId);
                    setSessionId(actualSessionId);

                    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
                      if (!persistentDataRequestedRef.current) {
                        const persistentDataPayload = {
                          action: 'persistent_data',
                          user_cognito_id: getAuthenticatedUserId(),
                          session_id: actualSessionId,
                          conversation_id: convId,
                        };
                        socketRef.current.send(JSON.stringify(persistentDataPayload));
                        persistentDataRequestedRef.current = true;
                      }
                    }
                  }
                } catch (error) {
                  console.error('❌ [Token Response] Failed to fetch session_id:', error);
                }
              })();
            }

            if (!userInitiatedStopRef.current && !window.isStoppingSession) {
              const validToken = getStoredToken();
              if (validToken) {
                if (!assemblySocketRef.current || assemblySocketRef.current.readyState !== WebSocket.OPEN) {
                  startTranscriptionStreamWithToken(validToken);
                }
              } else {
                requestToken('renewal');
              }
            }
          } else {
            console.warn('❓ [Configuration] Unknown configuration status:', modelDetails.status);
          }
        }
      } catch (error) {
        if (message.data && message.data.length > 0) {
          logger.debug('WebSocket message parsing error', {
            component: COMPONENT_NAME,
            action: 'MESSAGE_PARSE_ERROR',
            error: error.message,
          });
        }
      }
    };

    // ========== SOCKET ONCLOSE HANDLER ==========
    socket.onclose = (event) => {
      const closeDetails = {
        code: event.code,
        reason: event.reason || 'No reason provided',
        wasClean: event.wasClean,
      };

      logger.warn('Main WebSocket connection closed', {
        Event: event,
        component: COMPONENT_NAME,
        action: 'WEBSOCKET_CLOSED',
        userId: getAuthenticatedUserId(),
        context: {
          conversationId: conversationId || getCookie('conversation_id'),
          sessionId,
          online: navigator.onLine,
        },
        closeDetails,
      });
      webSocketConnectedRef.current = false;
      if (wsConnectLatchRef.current.key === key) {
        wsConnectLatchRef.current.inFlight = false;
      }
      console.log('🔌 [WebSocket] Connection closed:', event.code, event.reason);

      // Check if there's an active session that should trigger reconnection
      const isLiveSessionCookie = getCookie('isLiveSession') === 'true';
      // Code 1005: No Status Received, Code 1006: Abnormal Closure
      if (
        (event.code === 1005 || event.code === 1006) &&
        isLiveSessionCookie &&
        !userInitiatedStopRef.current &&
        !window.isStoppingSession &&
        navigator.onLine
      ) {
        reconnectWebSocketLocal();
      }
    };

    // ========== SOCKET ONERROR HANDLER ==========
    socket.onerror = (error) => {
      const errorDetails = {
        message: error.message || 'Unknown WebSocket error',
        type: error.type || 'error',
        eventPhase: error.eventPhase,
        isTrusted: error.isTrusted,
        timeStamp: error.timeStamp,
        readyState: socket.readyState,
        readyStateLabel: ['CONNECTING', 'OPEN', 'CLOSING', 'CLOSED'][socket.readyState] || 'UNKNOWN',
        url: socket.url,
        protocol: socket.protocol || 'none',
        extensions: socket.extensions || 'none',
        bufferedAmount: socket.bufferedAmount,
        binaryType: socket.binaryType,
      };

      logger.error('Main WebSocket connection error', {
        component: COMPONENT_NAME,
        action: 'WEBSOCKET_ERROR',
        userId: getAuthenticatedUserId(),
        context: {
          conversationId: conversationId || getCookie('conversation_id'),
          sessionId,
          key,
          connectionAttemptTime: new Date().toISOString(),
          browserInfo: {
            userAgent: navigator.userAgent,
            online: navigator.onLine,
            connectionType: navigator.connection?.effectiveType || 'unknown',
          },
        },
        error: errorDetails,
      });
      console.error('❌ [WebSocket] Connection error:', error);
      webSocketConnectedRef.current = false;
      if (wsConnectLatchRef.current.key === key) {
        wsConnectLatchRef.current.inFlight = false;
      }
    };

    // ========== CLEANUP FUNCTION ==========
    return () => {
      if (socket.pingInterval) {
        clearInterval(socket.pingInterval);
      }

      // Check if session is still active - if so, don't close WebSocket during navigation
      const recordingStatus = getCookie('recording_status');
      const isActiveSession = getCookie('conversation_active') === 'true';
      if (isActiveSession || recordingStatus === '🎙️ Listening...' || recordingStatus === 'Paused') {
        return;
      }

      if (socketRef.current) {
        const currentState = socketRef.current.readyState;
        if (currentState === WebSocket.OPEN && webSocketConnectedRef.current) {
          const connectionAge = Date.now() - (socketRef.lastOpenTime || 0);
          if (connectionAge < 500) {
            return;
          }
        } else if (currentState === WebSocket.CONNECTING) {
          return;
        }
        try {
          socketRef.current.onclose = null;
          socketRef.current.onerror = null;
        } catch {}
        try {
          if (socketRef.current.readyState === WebSocket.OPEN || socketRef.current.readyState === WebSocket.CLOSING) {
            socketRef.current.close();
          }
        } catch {}
        socketRef.current = null;
        webSocketConnectedRef.current = false;
      }
    };
  }, [sessionId, conversationId, conversationIdFromUrl, userId]);
};
